// i01 Iftor — Ramadan iftor gathering variant.
// Quiet, religious, hospitable. Sand paper, deep brown accents, Marcellus
// type. Hero is dusk: a soft warm-to-cool gradient sky carrying a glowing
// crescent and a slim minaret. The page makes room for two unique
// religious sections — the iftor menu (xurmo, palov, somsa, …) and namaz
// vaqtlari (Maghrib + Isha for that date in Tashkent).
//
// Music is intentionally absent — the only sound is a gentle chime on RSVP.

import { useEffect, useRef, useState } from "react";
import type { VariantProps } from "./types";
import {
  CountdownSection,
  StorySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";

const MENU: Array<{ name: string; note: string }> = [
  { name: "Xurmo va suv", note: "Ro'zani ochish — sunnat" },
  { name: "Palov", note: "An'anaviy Toshkent palovi" },
  { name: "Somsa", note: "Tandirda pishirilgan, qo'y go'shti" },
  { name: "Sho'rva", note: "Issiq, ziravorli" },
  { name: "Manti", note: "Bug'da pishirilgan, qatiq bilan" },
  { name: "Choy va shirinliklar", note: "Halvoyitar, parvarda, qand" },
];

const PRAYER_TIMES: Array<{ name: string; time: string; note: string }> = [
  { name: "Maghrib", time: "19:14", note: "Iftor — ro'zani ochish vaqti" },
  { name: "Isha", time: "20:42", note: "Tarovih namazi" },
];

export function IftarVariant({ t, isDark, ctx }: VariantProps) {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [glow, setGlow] = useState(false);

  // Crescent breathes — gently brightens then dims, infinite, reduced-motion off.
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    setGlow(true);
  }, []);

  return (
    <div className="v-i01">
      {/* HERO — twilight sky with crescent + minaret silhouette */}
      <section
        className="hero-fill v-i01-hero"
        ref={heroRef}
        style={{
          minHeight: "100vh",
          padding: "0",
          background: `linear-gradient(180deg, #2A2014 0%, #4B3220 25%, #7B5430 55%, ${t.bg} 100%)`,
          color: "#F8EBC8",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Stars dotted across the upper sky */}
        <svg
          aria-hidden
          viewBox="0 0 800 300"
          preserveAspectRatio="xMidYMid slice"
          style={{ position: "absolute", inset: 0, width: "100%", height: "55%" }}
        >
          <g fill="#F8EBC8" opacity="0.55">
            <circle cx="60" cy="40" r="0.9" />
            <circle cx="160" cy="60" r="1.1" />
            <circle cx="220" cy="30" r="0.8" />
            <circle cx="320" cy="80" r="1.4" />
            <circle cx="420" cy="50" r="0.7" />
            <circle cx="540" cy="70" r="1" />
            <circle cx="640" cy="34" r="1.2" />
            <circle cx="720" cy="90" r="0.8" />
          </g>
        </svg>

        {/* Crescent moon — large, glowing */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "8%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(180px, 28vw)",
            aspectRatio: "1",
            filter: glow ? "drop-shadow(0 0 18px rgba(248,235,200,0.55))" : "drop-shadow(0 0 8px rgba(248,235,200,0.25))",
            transition: "filter 4000ms ease-in-out",
            animation: glow ? "i01-moon-glow 4500ms ease-in-out infinite" : undefined,
          }}
        >
          <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
            <circle cx="50" cy="50" r="42" fill="#F8EBC8" />
            <circle cx="62" cy="44" r="38" fill="#4B3220" />
          </svg>
        </div>

        {/* Minaret silhouette — far right */}
        <svg
          aria-hidden
          viewBox="0 0 200 600"
          preserveAspectRatio="xMaxYMax meet"
          style={{
            position: "absolute",
            right: "4%",
            bottom: 0,
            height: "70%",
            width: "auto",
            opacity: 0.92,
          }}
        >
          <g fill="#1F1A14">
            <rect x="92" y="380" width="16" height="220" />
            <path d="M 84 380 L 116 380 L 110 350 L 90 350 Z" />
            <rect x="96" y="280" width="8" height="70" />
            <path d="M 88 280 Q 100 250 112 280 Z" />
            <path d="M 98 250 L 102 250 L 100 200 Z" />
            <path d="M 96 198 A 4 4 0 1 0 100 198 A 3 3 0 1 1 97 198 Z" fill="#F8EBC8" />
          </g>
        </svg>

        <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(20,12,8,0.45), rgba(20,12,8,0))", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1, padding: "0 24px", maxWidth: 720 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              letterSpacing: "0.1em",
              color: "#F8EBC8",
              opacity: 0.92,
              marginBottom: 8,
            }}
          >
            رَمَضَان كَرِيم
          </div>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: "#F8EBC8",
              opacity: 1,
              marginBottom: 28,
            }}
          >
            RAMADAN · IFTOR
          </div>
          <h1
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(40px, 8vw, 84px)",
              lineHeight: 1.05,
              color: "#F8EBC8",
            }}
          >
            {t.names}
          </h1>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 18,
              opacity: 0.85,
              marginTop: 16,
            }}
          >
            Bismillahir rohmanir rohim
          </div>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.32em",
              marginTop: 24,
              opacity: 0.85,
            }}
          >
            {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
          </div>
        </div>

        <style>{`
          @keyframes i01-moon-glow {
            0%   { filter: drop-shadow(0 0 8px rgba(248,235,200,0.25)); }
            50%  { filter: drop-shadow(0 0 22px rgba(248,235,200,0.65)); }
            100% { filter: drop-shadow(0 0 8px rgba(248,235,200,0.25)); }
          }
          .v-i01 a:focus-visible,
          .v-i01 button:focus-visible {
            outline: 2px solid ${t.accent};
            outline-offset: 2px;
          }
          @media (max-width: 768px) {
            .v-i01-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
            .v-i01-hero h1 { font-size: clamp(36px, 12vw, 84px) !important; }
            .v-i01-section { padding: 64px 18px !important; }
            .v-i01-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
            .v-i01 button, .v-i01 a { min-height: 44px; }
          }
        `}</style>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow="Ramazon oqshomi"
        title="Bizning iftorimizga marhamat"
        body="Karimovlar oilasi sizni — qadrli mehmonlarini — Ramazon oyining muborak iftor dasturxoniga taklif etadi. Ro'zani birga ochib, namoz o'qib, dasturxon atrofida sizning bilan birga vaqt o'tkazish biz uchun katta sharafdir. Allohning rahmati va barakati barchamizga yor bo'lsin."
      />

      {/* UNIQUE: Iftor menusi */}
      <section
        className="v-i01-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: t.bg,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            DASTURXON
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(28px, 5vw, 44px)",
              marginBottom: 48,
            }}
          >
            Iftor menyusi
          </h2>
          <div
            className="v-i01-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 18,
              textAlign: "left",
            }}
          >
            {MENU.map((m, i) => (
              <div
                key={m.name}
                style={{
                  padding: "20px 22px",
                  background: t.bg,
                  border: `1px solid ${t.accent}40`,
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 16,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: `${t.accent}20`,
                    color: t.accent,
                    fontFamily: t.font,
                    fontSize: 16,
                    display: "grid",
                    placeItems: "center",
                    fontFeatureSettings: '"lnum" 1',
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: t.font,
                      fontSize: 18,
                      lineHeight: 1.2,
                    }}
                  >
                    {m.name}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontStyle: "italic",
                      fontSize: 13,
                      opacity: 0.7,
                      marginTop: 4,
                    }}
                  >
                    {m.note}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UNIQUE: Namoz vaqtlari */}
      <section
        className="v-i01-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}12`,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            NAMOZ VAQTLARI
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(26px, 4.5vw, 38px)",
              marginBottom: 12,
            }}
          >
            Toshkent · {t.date}
          </h2>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 15,
              opacity: 0.7,
              marginBottom: 40,
            }}
          >
            Maghrib namazidan so&apos;ng iftor qilinadi
          </div>
          <div
            className="v-i01-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 16,
            }}
          >
            {PRAYER_TIMES.map((p) => (
              <div
                key={p.name}
                style={{
                  padding: "32px 24px",
                  background: t.bg,
                  border: `1px solid ${t.accent}55`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.32em",
                    color: t.accent,
                    marginBottom: 12,
                  }}
                >
                  {p.name.toUpperCase()}
                </div>
                <div
                  style={{
                    fontFamily: t.font,
                    fontSize: 44,
                    lineHeight: 1,
                    color: t.ink,
                    fontFeatureSettings: '"lnum" 1, "tnum" 1',
                  }}
                >
                  {p.time}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 13,
                    opacity: 0.7,
                    marginTop: 12,
                  }}
                >
                  {p.note}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ScheduleSection
        t={t}
        items={[
          ["18:30", "Mehmonlar yig'ilishi"],
          ["19:14", "Maghrib · iftor"],
          ["19:30", "Namoz"],
          ["20:00", "Asosiy dasturxon — palov"],
          ["20:42", "Isha · tarovih namazi"],
          ["21:30", "Choy va suhbat"],
          ["22:30", "Yakun"],
        ]}
      />

      <MapSection t={t} isDark={isDark} addressLine={t.venue} />

      <RsvpSection
        t={t}
        isDark={isDark}
        {...ctx.rsvp}
        label="Bizning iftorimizga marhamat"
      />

      <ShareSection t={t} />

      <GuestFooter t={t} />
    </div>
  );
}
